import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Box,
    Text,
    Input,
    InputGroup,
    InputLeftElement,
    VStack
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useGetLocations } from '../hooks/useGetLocations';
import { useMemo, useState } from 'react';
import { useUpdateEmsLocation } from '../hooks/useUpdateEmsLocation';
import { useQueryClient } from '@tanstack/react-query';
import { GetEmsResponse } from '../hooks/useGetEms';

interface EditLocationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onParentClose?: () => void;
    emsId: string;
}

const EditLocationModal = ({ isOpen, onClose, onParentClose, emsId }: EditLocationModalProps) => {
    const { data } = useGetLocations(true);
    const updateEmsLocation = useUpdateEmsLocation();
    const queryClient = useQueryClient();
    const [selectedLocation, setSelectedLocation] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const locationsToDisplay = useMemo(() => (
        data?.locations?.filter(f => f._id !== 'all') ?? []
    ), [data]);

    const filteredLocations = useMemo(() => {
        if (!searchTerm.trim()) return locationsToDisplay;
        return locationsToDisplay.filter(location =>
            location.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [locationsToDisplay, searchTerm]);

    const handleLocationSelect = (value: string) => {
        setSelectedLocation(value);
        setShowConfirmation(true);
    };

    const handleConfirm = async () => {
        if (!selectedLocation) return;

        try {
            await updateEmsLocation.mutateAsync({
                emsId,
                locationId: selectedLocation
            });

            const selectedLocationTitle = locationsToDisplay.find(loc => loc._id === selectedLocation)?.title;
            if (selectedLocationTitle) {
                queryClient.setQueryData<GetEmsResponse>(['/ems/get', emsId], (oldData) => {
                    if (oldData) {
                        return {
                            ...oldData,
                            location: selectedLocationTitle
                        };
                    }
                    return oldData;
                });
            }

            onClose();
            onParentClose?.();
            queryClient.invalidateQueries({ queryKey: ['/ems/orders/feed'] });
            queryClient.invalidateQueries({ queryKey: ['/ems/orders/feed-paginated'] });
            setSelectedLocation('');
            setShowConfirmation(false);
        } catch (error) {
            // Error handling is done by the hook
        }
    };

    const handleCancel = () => {
        setSelectedLocation('');
        setShowConfirmation(false);
    };

    const handleClose = () => {
        setSelectedLocation('');
        setShowConfirmation(false);
        setSearchTerm('');
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                {/* Nested list scrolls, not the modal body — no header divider. */}
                <ModalHeader borderBottomWidth={0}>Edit Location</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    <FormControl>
                        <FormLabel>Location</FormLabel>
                        <InputGroup size="md" mb={3}>
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color="gray.400" />
                            </InputLeftElement>
                            <Input
                                placeholder="Search locations..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                bg="white"
                                borderColor="gray.300"
                                _hover={{ borderColor: 'gray.400' }}
                                _focus={{ borderColor: '#0071E3', boxShadow: '0 0 0 1px #0071E3' }}
                            />
                        </InputGroup>
                        <Box
                            border="1px solid"
                            borderColor="gray.300"
                            borderRadius="md"
                            h="200px"
                            overflowY="auto"
                            overflowX="hidden"
                            overscrollBehavior="contain"
                            bg="white"
                            tabIndex={0}
                            onWheel={(e) => e.stopPropagation()}
                            onTouchMove={(e) => e.stopPropagation()}
                            sx={{
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: '#f1f1f1',
                                    borderRadius: '4px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: '#cbd5e0',
                                    borderRadius: '4px',
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    background: '#a0aec0',
                                },
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#cbd5e0 #f1f1f1',
                            }}
                        >
                            <VStack align="stretch" spacing={0}>
                                {filteredLocations.length > 0 ? (
                                    filteredLocations.map((location) => (
                                        <Box
                                            key={location._id}
                                            px={4}
                                            py={2}
                                            cursor="pointer"
                                            bg={selectedLocation === location._id ? 'blue.50' : 'white'}
                                            borderBottom="1px solid"
                                            borderColor="gray.100"
                                            _hover={{ bg: selectedLocation === location._id ? 'blue.50' : 'gray.50' }}
                                            _last={{ borderBottom: 'none' }}
                                            onClick={() => handleLocationSelect(location._id)}
                                        >
                                            <Text
                                                fontSize="sm"
                                                color={selectedLocation === location._id ? '#0071E3' : 'gray.700'}
                                                fontWeight={selectedLocation === location._id ? 'medium' : 'normal'}
                                            >
                                                {location.title}
                                            </Text>
                                        </Box>
                                    ))
                                ) : (
                                    <Box px={4} py={4} textAlign="center">
                                        <Text fontSize="sm" color="gray.500">
                                            No locations found
                                        </Text>
                                    </Box>
                                )}
                            </VStack>
                        </Box>
                    </FormControl>

                    {showConfirmation && (
                        <Box mt={4} p={4} bg="blue.50" borderRadius="md">
                            <Text fontSize="sm" color="blue.800">
                                Are you sure you want to change the location to "{locationsToDisplay.find(loc => loc._id === selectedLocation)?.title}"?
                            </Text>
                        </Box>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button size="action" variant="outlineGray" mr={3} onClick={handleCancel}>
                        Cancel
                    </Button>
                    {showConfirmation && (
                        <Button
                            size="action"
                            bg="#0071E3"
                            color="white"
                            _hover={{ bg: '#3544a0' }}
                            onClick={handleConfirm}
                            isLoading={updateEmsLocation.isPending}
                            loadingText="Updating..."
                        >
                            Confirm
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default EditLocationModal;
